import React, { useEffect, useRef, useState } from "react";
import { adminApi, formatApiError, useAuth } from "../../../context/AuthContext";
import { useBranding } from "../../../context/BrandingContext";
import { Button } from "../../ui/button";
import { toast } from "sonner";
import { Loader2, Upload, Trash2, Image as ImageIcon, RefreshCw } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LOGO_ACCEPT = "image/png,image/jpeg,image/jpg,image/webp";
const FAVICON_ACCEPT = "image/png,image/jpeg,image/jpg,image/webp,image/x-icon,image/vnd.microsoft.icon,.ico";

function bytesLabel(n) {
  if (!n) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}

function buildUrl(u) {
  if (!u) return null;
  if (/^https?:\/\//i.test(u)) return u;
  return `${BACKEND_URL}${u}`;
}

/**
 * Phase 18.6 — Branding section.
 *
 * Two cards: Website-logo + Favicon. Owner/Admin can upload/replace/delete;
 * Manager + Content Manager get a read-only view. Backend enforces RBAC.
 */
export default function BrandingSection() {
  const { user } = useAuth();
  const branding = useBranding();
  const canEdit = ["owner", "admin"].includes(user?.role);
  const canView = canEdit || ["manager", "content_manager"].includes(user?.role);

  const [data, setData] = useState({ logo: null, favicon: null, footer_logo: null, homepage_hero: null, metadata: null, updated_at: null });
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [metaDraft, setMetaDraft] = useState({
    site_title: "",
    site_tagline: "",
    browser_title_template: "",
    default_meta_description: "",
  });
  const [metaSaving, setMetaSaving] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await adminApi.get("/admin/branding");
      const payload = r.data || {};
      setData(payload);
      if (payload.metadata) {
        setMetaDraft({
          site_title: payload.metadata.site_title || "",
          site_tagline: payload.metadata.site_tagline || "",
          browser_title_template: payload.metadata.browser_title_template || "",
          default_meta_description: payload.metadata.default_meta_description || "",
        });
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  };

  useEffect(() => { if (canView) refresh(); /* eslint-disable-next-line */ }, []);

  const uploadKind = async (kind, file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await adminApi.post(`/admin/branding/${kind}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setData(r.data);
      const lbl = { logo: "Logo", favicon: "Favicon", footer_logo: "Footer logo", homepage_hero: "Homepage hero" }[kind] || "Asset";
      toast.success(`${lbl} geüpload`);
      branding.refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusy(false); }
  };

  const deleteKind = async (kind) => {
    const labels = {
      logo: "Logo verwijderen? De site valt dan terug op het standaard 5999Cargo logo.",
      favicon: "Favicon verwijderen? De browser valt dan terug op de standaard favicon.",
      footer_logo: "Footer logo verwijderen? Footer valt dan terug op het header-logo.",
      homepage_hero: "Homepage hero foto verwijderen? Homepage valt dan terug op de premium gradient.",
    };
    if (!window.confirm(labels[kind] || "Asset verwijderen?")) return;
    setBusy(true);
    try {
      const r = await adminApi.delete(`/admin/branding/${kind}`);
      setData(r.data);
      const lbl = { logo: "Logo", favicon: "Favicon", footer_logo: "Footer logo", homepage_hero: "Homepage hero" }[kind] || "Asset";
      toast.success(`${lbl} verwijderd`);
      branding.refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusy(false); }
  };

  const saveMetadata = async () => {
    setMetaSaving(true);
    try {
      const r = await adminApi.put("/admin/branding/metadata", metaDraft);
      setData(r.data);
      toast.success("Site-titel en slogan bijgewerkt");
      branding.refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setMetaSaving(false); }
  };

  if (!canView) {
    return (
      <div className="bg-white border border-gray-200 p-10 text-center" data-testid="branding-no-access">
        <h2 className="font-chivo font-black text-xl text-[#0A192F]">Geen toegang tot branding.</h2>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="branding-section">
      <div className="bg-white border border-gray-200 p-6">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h2 className="font-chivo font-bold text-lg text-[#0A192F]">Website branding</h2>
            <p className="text-sm text-gray-600 mt-1 max-w-3xl">
              Beheer hier het actieve website-logo en de favicon. Wijzigingen
              zijn direct zichtbaar voor bezoekers na een verversing van de
              pagina. Verwijderen valt automatisch terug op het standaard
              5999Cargo logo / standaard favicon.
            </p>
          </div>
          <Button
            variant="outline"
            onClick={refresh}
            className="rounded-none"
            disabled={loading}
            data-testid="branding-refresh"
          >
            <RefreshCw className="w-4 h-4 mr-2" /> Verversen
          </Button>
        </div>

        {!canEdit && (
          <p className="mt-3 text-xs text-amber-700">
            U heeft alleen leesrechten — vraag Owner of Admin om de branding te wijzigen.
          </p>
        )}

        {loading ? (
          <div className="py-10 text-center text-gray-500">
            <Loader2 className="w-4 h-4 inline animate-spin mr-2" /> Laden...
          </div>
        ) : (
          <div className="mt-6 grid md:grid-cols-2 gap-6">
            <BrandingCard
              testid="branding-logo"
              title="Website-logo (header)"
              help="PNG, JPG of WEBP — max 2 MB. Wordt getoond in de header."
              accept={LOGO_ACCEPT}
              previewBg="bg-[#082c50]"
              fallbackUrl={null}
              asset={data.logo}
              canEdit={canEdit}
              busy={busy}
              onUpload={(f) => uploadKind("logo", f)}
              onDelete={() => deleteKind("logo")}
            />
            <BrandingCard
              testid="branding-footer-logo"
              title="Footer logo"
              help="PNG, JPG of WEBP — max 2 MB. Aparte variant voor de donkere footer (witte of lichte logo aanbevolen). Als leeg, gebruikt de footer het header-logo."
              accept={LOGO_ACCEPT}
              previewBg="bg-[#082c50]"
              fallbackUrl={null}
              asset={data.footer_logo}
              canEdit={canEdit}
              busy={busy}
              onUpload={(f) => uploadKind("footer_logo", f)}
              onDelete={() => deleteKind("footer_logo")}
            />
            <BrandingCard
              testid="branding-homepage-hero"
              title="Homepage hero foto"
              help="PNG, JPG of WEBP — max 5 MB. Achtergrondfoto bovenaan de homepage. Aanbevolen 2000×1100 px. Als leeg, toont de homepage een premium blue gradient."
              accept={LOGO_ACCEPT}
              previewBg="bg-[#082c50]"
              fallbackUrl={null}
              asset={data.homepage_hero}
              canEdit={canEdit}
              busy={busy}
              onUpload={(f) => uploadKind("homepage_hero", f)}
              onDelete={() => deleteKind("homepage_hero")}
            />
            <BrandingCard
              testid="branding-favicon"
              title="Favicon"
              help="ICO of PNG (transparant) — max 1 MB. Wordt getoond in browser-tabs."
              accept={FAVICON_ACCEPT}
              previewBg="bg-gray-100"
              fallbackUrl={null}
              asset={data.favicon}
              canEdit={canEdit}
              busy={busy}
              onUpload={(f) => uploadKind("favicon", f)}
              onDelete={() => deleteKind("favicon")}
              square
            />
          </div>
        )}
      </div>

      {/* Phase 18.15.4 — Site metadata (browser tab title + tagline) */}
      <div className="bg-white border border-gray-200 p-6" data-testid="branding-metadata-card">
        <div className="mb-3">
          <h2 className="font-chivo font-bold text-lg text-[#0A192F]">
            Browser tab &amp; SEO metadata
          </h2>
          <p className="text-sm text-gray-600 mt-1 max-w-3xl">
            Beheer de titel, slogan en standaard meta description die in de
            browser-tab en zoekresultaten verschijnen. Wijzigingen werken op
            alle publieke pagina's na een ververser.
          </p>
        </div>
        {!canEdit && (
          <p className="text-xs text-amber-700 mb-2">
            U heeft alleen leesrechten — vraag Owner of Admin om aanpassingen.
          </p>
        )}
        <div className="grid md:grid-cols-2 gap-4 mt-3">
          <label className="block">
            <span className="text-xs uppercase tracking-wide font-bold text-gray-600">
              Site title
            </span>
            <input
              type="text"
              value={metaDraft.site_title}
              onChange={(e) => setMetaDraft((d) => ({ ...d, site_title: e.target.value }))}
              disabled={!canEdit || metaSaving}
              className="mt-1 w-full px-3 py-2 border border-gray-300 text-sm focus:outline-none focus:border-[#082c50]"
              placeholder="5999Cargo Group Europe"
              data-testid="branding-metadata-site-title"
            />
          </label>
          <label className="block">
            <span className="text-xs uppercase tracking-wide font-bold text-gray-600">
              Site tagline / slogan
            </span>
            <input
              type="text"
              value={metaDraft.site_tagline}
              onChange={(e) => setMetaDraft((d) => ({ ...d, site_tagline: e.target.value }))}
              disabled={!canEdit || metaSaving}
              className="mt-1 w-full px-3 py-2 border border-gray-300 text-sm focus:outline-none focus:border-[#082c50]"
              placeholder="Cargo with a smile."
              data-testid="branding-metadata-tagline"
            />
          </label>
          <label className="block md:col-span-2">
            <span className="text-xs uppercase tracking-wide font-bold text-gray-600">
              Browser tab title template
            </span>
            <input
              type="text"
              value={metaDraft.browser_title_template}
              onChange={(e) => setMetaDraft((d) => ({ ...d, browser_title_template: e.target.value }))}
              disabled={!canEdit || metaSaving}
              className="mt-1 w-full px-3 py-2 border border-gray-300 text-sm focus:outline-none focus:border-[#082c50]"
              placeholder="{page_title} | 5999Cargo"
              data-testid="branding-metadata-template"
            />
            <span className="text-[11px] text-gray-500 mt-1 block">
              Gebruik <code className="text-[#082c50] font-mono">{"{page_title}"}</code> als placeholder voor de pagina-titel.
            </span>
          </label>
          <label className="block md:col-span-2">
            <span className="text-xs uppercase tracking-wide font-bold text-gray-600">
              Default meta description (SEO)
            </span>
            <textarea
              rows={3}
              value={metaDraft.default_meta_description}
              onChange={(e) => setMetaDraft((d) => ({ ...d, default_meta_description: e.target.value }))}
              disabled={!canEdit || metaSaving}
              className="mt-1 w-full px-3 py-2 border border-gray-300 text-sm focus:outline-none focus:border-[#082c50]"
              placeholder="Verzend pakketten, dozen, pallets..."
              data-testid="branding-metadata-description"
            />
          </label>
        </div>
        {canEdit && (
          <div className="flex justify-end mt-4">
            <Button
              onClick={saveMetadata}
              disabled={metaSaving}
              className="bg-[#082c50] hover:bg-[#1a5193] text-white rounded-none font-chivo font-bold uppercase text-xs"
              data-testid="branding-metadata-save"
            >
              {metaSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Metadata opslaan
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

function BrandingCard({
  testid, title, help, accept, previewBg, asset, canEdit, busy,
  onUpload, onDelete, square,
}) {
  const inputRef = useRef(null);
  const [picking, setPicking] = useState(false);

  const url = asset ? buildUrl(asset.url) : null;

  return (
    <div className="border border-gray-200 p-4" data-testid={testid}>
      <h3 className="font-chivo font-bold text-[#0A192F]">{title}</h3>
      <p className="text-xs text-gray-500 mt-1">{help}</p>

      <div
        className={`mt-3 ${previewBg} border border-gray-200 flex items-center justify-center overflow-hidden ${
          square ? "h-32 w-32 mx-auto" : "h-32 px-4"
        }`}
        data-testid={`${testid}-preview`}
      >
        {url ? (
          <img
            src={url}
            alt={title}
            className={square ? "max-h-24 max-w-24 object-contain" : "max-h-24 max-w-full object-contain"}
            data-testid={`${testid}-preview-img`}
          />
        ) : (
          <div className="text-gray-400 text-xs flex flex-col items-center gap-2" data-testid={`${testid}-empty`}>
            <ImageIcon className="w-6 h-6" />
            <span>Standaard fallback actief</span>
          </div>
        )}
      </div>

      {asset && (
        <dl className="mt-3 text-xs text-gray-600 space-y-1" data-testid={`${testid}-meta`}>
          <div className="flex gap-2"><dt className="w-24 text-gray-400">Bestand</dt><dd className="font-mono break-all">{asset.file_name}</dd></div>
          <div className="flex gap-2"><dt className="w-24 text-gray-400">Type</dt><dd className="font-mono">{asset.mime_type}</dd></div>
          <div className="flex gap-2"><dt className="w-24 text-gray-400">Grootte</dt><dd>{bytesLabel(asset.size_bytes)}</dd></div>
          <div className="flex gap-2"><dt className="w-24 text-gray-400">Geüpload</dt><dd>{asset.uploaded_at && new Date(asset.uploaded_at).toLocaleString("nl-NL")}</dd></div>
          {asset.uploaded_by && (
            <div className="flex gap-2"><dt className="w-24 text-gray-400">Door</dt><dd>{asset.uploaded_by}</dd></div>
          )}
        </dl>
      )}

      {canEdit && (
        <div className="mt-4 flex flex-wrap gap-2">
          <input
            ref={inputRef}
            type="file"
            accept={accept}
            className="hidden"
            data-testid={`${testid}-input`}
            onChange={(e) => {
              const f = e.target.files?.[0];
              e.target.value = "";
              if (f) { setPicking(false); onUpload(f); }
            }}
          />
          <Button
            disabled={busy}
            className="rounded-none bg-[#082c50] hover:bg-[#1a5193] text-white"
            onClick={() => { setPicking(true); inputRef.current?.click(); }}
            data-testid={`${testid}-upload`}
          >
            {busy && picking ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
            {asset ? "Vervangen" : "Uploaden"}
          </Button>
          {asset && (
            <Button
              variant="outline"
              disabled={busy}
              onClick={onDelete}
              className="rounded-none text-red-700 border-red-300 hover:bg-red-50"
              data-testid={`${testid}-delete`}
            >
              <Trash2 className="w-4 h-4 mr-2" /> Verwijderen
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
